import {useCallback, useMemo} from 'react';

import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';

import {media, flipOnLTR} from '~/shared/theme/utils';
import {getLocalizationService} from '~/shared/services/localisationService';
import {
  body16Bold,
  body14Normal,
  body16Normal,
  headingS,
  headingM,
  subheadingSBold,
  headingXS,
} from '~/shared/theme/typography';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {
  isCollectionNameInCollectionsList,
} from '~/shared/consts/customGroupsConsts';
import {MiniFeedQueries, RestaurantsGroupTypes} from '~/shared/consts/restaurantConsts';
import {
  selectCollectionsList,
  selectCurrentCuisineId,
  selectMainRestaurantsCuisinesCounts,
  selectSelectedFilters,
} from '~/shared/store/selectors';
import storesIconUrl from '~/assets/images/icons/stores.svg';
import useResolvedMiniFeedQuery from '~/shared/hooks/useResolvedMiniFeedQuery';
import {convertGapToMargin} from '~/shared/theme/convertGapToMargin';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {HEADER_HEIGHT, HEADER_HEIGHT_MOBILE, NEW_DESIGN_RES_LIST_TITLE_SIZES} from '~/shared/consts/vlConsts';
import {resetRestaurantsFilter} from '~/shared/store/storeModules/ui/uiActions';
import {trackEvent} from '~/shared/services/analytics';
import {removeQueries} from '~/shared/router';

import {ICarouselItem, IGroupItem} from '../models/RestaurantsListItemType';

import GroupHeaderWithClear from './GroupHeaderWithClear';

export const MARKETING_BANNER_ROW_HEIGHT_DESKTOP_NEW_DESIGN = 322;
export const MARKETING_BANNER_ROW_HEIGHT_TABLET_NEW_DESIGN = 246;
export const MARKETING_BANNER_ROW_HEIGHT_LARGE_MOBILE_NEW_DESIGN = 175;
export const MARKETING_BANNER_ROW_HEIGHT_SMALL_MOBILE_NEW_DESIGN = 226;

export const ItemGroupSubheaderHeight = 22;

export const ItemGroupHeight = {
  mobile: HEADER_HEIGHT_MOBILE,
  rest: HEADER_HEIGHT,
};

const NEW_DESIGN_COLLECTION_PAGE_TITLE_SIZES = {
  default: 88,
  largeMobile: 88,
  largeTablet: 92,
};

export const getGroupItemRootHeight = ({
  hasSubheader,
  isCustomGroup,
  isMinLargeMobile,
  isMinLargeTablet,
}: {
  hasSubheader: boolean;
  isCustomGroup?: boolean;
  isMinLargeMobile?: boolean;
  isMinLargeTablet?: boolean;
}) => {
  const subheaderAdditionalSpacing = hasSubheader ? ItemGroupSubheaderHeight : 0;
  const sizesCollection = isCustomGroup ? NEW_DESIGN_COLLECTION_PAGE_TITLE_SIZES : NEW_DESIGN_RES_LIST_TITLE_SIZES;

  if (isMinLargeTablet) {
    return sizesCollection.largeTablet + subheaderAdditionalSpacing;
  }

  if (isMinLargeMobile) {
    return sizesCollection.largeMobile + subheaderAdditionalSpacing;
  }

  return sizesCollection.default + subheaderAdditionalSpacing;
};

const groupItemTitleKeysMapping = {
  new: 'new_and_recommended_restaurants',
  pooled: 'pooled_order_restaurant',
  open: 'all_businesses',
  closed: 'closed_businesses',
  discountCoupons: 'restaurant_with_discount_in_your_area',
  notPooledRestaurants: 'additional_restaurant_without_pooled_order',
  closedWithFutureOrderStartingTomorrow: 'closed_with_future_order_starting_tomorrow',
};

const Root = styled.div<{hasSubheader: boolean; isCustomGroup?: boolean}>`
  padding: ${({isCustomGroup}) => (isCustomGroup ? '32px 16px 24px' : '32px 16px 20px')};
  display: flex;
  align-items: ${({isCustomGroup}) => (isCustomGroup ? 'flex-start' : 'flex-end')};
  width: 100%;
  height: ${({hasSubheader, isCustomGroup}) => getGroupItemRootHeight({
    hasSubheader,
    isCustomGroup,
  })}px;
  ${({hasSubheader, isCustomGroup}) => media.minLargeMobile`
    height: ${getGroupItemRootHeight({hasSubheader, isCustomGroup, isMinLargeMobile: true})}px;
    padding: ${isCustomGroup ? '32px 0 24px' : '32px 0 22px'};
  `}
  ${({hasSubheader, isCustomGroup}) => media.minTablet`
    height: ${getGroupItemRootHeight({hasSubheader, isCustomGroup, isMinLargeMobile: true})}px;
    padding: ${isCustomGroup ? '32px 0 24px' : '32px 0 20px'};
  `}
  ${({hasSubheader, isCustomGroup}) => media.minLargeTablet`
    height: ${getGroupItemRootHeight({hasSubheader, isCustomGroup, isMinLargeTablet: true})}px;
    padding: ${isCustomGroup ? '32px 0 24px' : '32px 0 20px'};
  `}
`;

const Headers = styled(FlexColumn)`
  width: 100%;
  ${flipOnLTR`
    text-align: right;
    margin-right: 0;
  `}
`;

const Header = styled.div<{isCustomGroup: boolean}>`
  display: flex;
  width: 100%;
  ${({theme}) => convertGapToMargin('0 6px', theme.isLTR)}
  ${({isCustomGroup}) => (isCustomGroup ? `
    ${headingS}
    line-height: 28px;
  ` : `
    ${subheadingSBold}
    line-height: 20px;
  `)}
  color: ${({theme}) => theme.colors.charcoal80};
  ${media.minLargeMobile`
    ${({isCustomGroup}) => (isCustomGroup ? `
      max-width: initial;
    ` : headingXS)}
  `}
  ${media.minLargeTablet`
    ${({isCustomGroup}) => `
      ${isCustomGroup ? headingM : headingXS}
      line-height: 28px;
    `}
  `}
`;

const Subheader = styled.p<{isCustomGroup: boolean}>`
  height: ${ItemGroupSubheaderHeight}px;
  color: ${({theme}) => theme.colors.secondary};
  margin-top: ${({isCustomGroup}) => (isCustomGroup ? 8 : 4)}px;
  ${flipOnLTR`
    text-align: right;
  `};
  ${({isCustomGroup}) => (isCustomGroup ? body16Bold : body14Normal)}
  line-height: 18px;
  ${media.minLargeMobile`
    ${({isCustomGroup}) => (isCustomGroup ? body16Bold : body16Normal)}
    margin-top: ${({isCustomGroup}) => (isCustomGroup ? 8 : 4)}px;
    line-height: 20px;
  `}
`;

const Icon = styled(ImageWithAlt).attrs({noAlt: true})`
  width: 32px;
`;

const NewIcon = styled(Icon)`
  ${flipOnLTR`
    margin-right: 6px;
  `}
`;

const HeaderTextContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

export type TGroupItem = IGroupItem & Partial<{
  className: string;
  listTopOffset: number;
  isAnyFilterOrSortSelected: boolean;
}> & Partial<ICarouselItem>;

const GroupItem = ({
  className,
  count,
  groupId,
  subTitleArgs,
  subTitleKey,
  title,
  subtitle,
  iconUrl,
  isAnyFilterOrSortSelected,
  withClearButtonSlot,
}: TGroupItem) => {
  const dispatch = useDispatch();
  const {t, currentLanguageKey} = getLocalizationService();
  const collectionsList = useSelector(selectCollectionsList);
  const resolvedMiniFeedQuery = useResolvedMiniFeedQuery();
  const isCustomGroup = isCollectionNameInCollectionsList(collectionsList, groupId);

  const {showStores} = useSelector(selectSelectedFilters);

  const cuisines = useSelector(selectMainRestaurantsCuisinesCounts);
  const activeCuisineId = useSelector(selectCurrentCuisineId);
  const activeCuisine = useMemo(() => {
    if (activeCuisineId) {
      return cuisines.find(({id}) => id === activeCuisineId) || null;
    }

    return null;
  }, [cuisines, activeCuisineId]);

  const isMiniFeedTitle = groupId === 'miniFeedTitle';

  const minifeedIconUrl = useMemo(() => {
    if (resolvedMiniFeedQuery && isMiniFeedTitle) {
      return activeCuisine ?
        activeCuisine.iconImageUrl :
        showStores ?
          storesIconUrl :
          null;
    }

    if (iconUrl) {
      return iconUrl;
    }

    return null;
  }, [iconUrl, activeCuisine, showStores, resolvedMiniFeedQuery, isMiniFeedTitle]);

  const headerText = useMemo(() => {
    if (isCustomGroup) {
      return title;
    }

    if (resolvedMiniFeedQuery && isMiniFeedTitle) {
      return activeCuisine ? activeCuisine.names[currentLanguageKey] :
        showStores ?
          t('shops') :
          '';
    }

    const headerTextKey = groupItemTitleKeysMapping[groupId as keyof typeof groupItemTitleKeysMapping];
    return t(headerTextKey, {postProcess: 'interval', count});
  }, [
    count,
    groupId,
    isCustomGroup,
    t,
    currentLanguageKey,
    title,
    activeCuisine,
    showStores,
    resolvedMiniFeedQuery,
    isMiniFeedTitle,
  ]);

  const subTitleText = useMemo(() => {
    if (groupId === 'closed') {
      return null;
    }

    if (isCustomGroup) {
      return t(subTitleKey, {...subTitleArgs as Record<string, unknown>});
    }

    return subtitle;
  }, [groupId, isCustomGroup, subtitle, subTitleArgs, subTitleKey, t]);

  const isCustomGroupStyle = isCustomGroup || (!!resolvedMiniFeedQuery && isMiniFeedTitle);

  const shouldRenderClearButton = withClearButtonSlot && isAnyFilterOrSortSelected;

  const onClearClick = useCallback(() => {
    dispatch(resetRestaurantsFilter());
    removeQueries([MiniFeedQueries.CUISINE]);
    trackEvent('filter_reset');
  }, [dispatch]);

  const newDesignHeaderTextContent = (
    <HeaderTextContent>
      {headerText}{' '}{minifeedIconUrl && <NewIcon src={minifeedIconUrl} />}
    </HeaderTextContent>
  );

  return (
    <Root
      className={className}
      id={groupId}
      hasSubheader={!!subTitleText}
      isCustomGroup={isCustomGroupStyle}
    >
      <Headers>
        <Header
          isCustomGroup={isCustomGroupStyle}
          data-test-id={groupId === RestaurantsGroupTypes.OPEN ? `open_${headerText}` : `title_${groupId}`}
        >
          <>
            {!shouldRenderClearButton ?
              newDesignHeaderTextContent :
              (<GroupHeaderWithClear onClearClick={onClearClick}>{newDesignHeaderTextContent}</GroupHeaderWithClear>)}
          </>
        </Header>
        {(subTitleText || subtitle) && <Subheader isCustomGroup={isCustomGroup}>{subTitleText || subtitle}</Subheader>}
      </Headers>
    </Root>
  );
};

export default GroupItem;
